import React, { useContext, useState, useEffect } from "react";
import AppBar from "@material-ui/core/AppBar";
import CssBaseline from "@material-ui/core/CssBaseline";
import Divider from "@material-ui/core/Divider";
import Drawer from "@material-ui/core/Drawer";
import Hidden from "@material-ui/core/Hidden";
import IconButton from "@material-ui/core/IconButton";
import Collapse from "@material-ui/core/Collapse";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import styled from "styled-components";

import {
  HistoryOutlined,
  PermIdentityOutlined,
  CreateOutlined,
  AssignmentIndOutlined,
  Visibility,
  LiveTv,
  AssessmentOutlined,
  AssignmentTurnedInOutlined,
  ExpandLess,
  ExpandMore,
  BusinessOutlined,
  PinDrop,
  ExitToApp,
  Call,
} from "@material-ui/icons";
import MenuIcon from "@material-ui/icons/Menu";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import { useTheme } from "@material-ui/core/styles";
import firebase from "../../Firebase/firebase";
import "firebase/firestore";
import { useHistory } from "react-router-dom";
import { BuildingContext, CallStatusENUM } from "Context/BuildingProvider";
import AgoraPTT from "Modules/Misc/AgoraPTT";
import { useStyles } from "Theme/styles";
import { device } from "Theme/device";
import { AuthContext } from "Firebase/AuthProvider";
import { VerticalSpacer } from "./StandardStyledComponents";

const ToolbarContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`;

const DesktopButtons = styled.div`
  display: flex;
  flex-direction: row;
`;

const ListContainer = styled.div`
  @media ${device.laptop} {
    padding: 100px 0 0 16px;
  }

  @media ${device.desktop} {
    padding: 100px 0 0 16px;
  }
`;

interface AppDrawerState {
  agoraOpen: boolean;
  callStatus?: CallStatusENUM;
}

const DEFAULT_STATE: AppDrawerState = {
  agoraOpen: false,
};

export default function AppDrawer(props: any) {
  const { window } = props;
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { buildingState } = useContext(BuildingContext);
  const { pageName } = useContext(AuthContext);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [buildingOpen, setBuildingOpen] = React.useState(false);
  const [dataOpen, setDataOpen] = React.useState(false);
  const [state, setState] = useState(DEFAULT_STATE);
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleBuildingClick = () => {
    setBuildingOpen(!buildingOpen);
  };

  const handleDataClick = () => {
    setDataOpen(!dataOpen);
  };

  const handleSignOutClick = (event: any) => {
    event.preventDefault();

    firebase
      .auth()
      .signOut()
      .then((res) => {
        history.push("/auth/login");
      });
  };

  const handleCallClick = () => {
    setState((prevState) => ({
      ...prevState,
      agoraOpen: !prevState.agoraOpen,
    }));
  };

  useEffect(() => {
    if (buildingState?.call_status === CallStatusENUM.OPEN) {
      setState({
        ...state,
        agoraOpen: true,
        callStatus: buildingState?.call_status,
      });
    } else if (buildingState?.call_status === CallStatusENUM.CLOSED) {
      setState({
        ...state,
        agoraOpen: false,
        callStatus: buildingState?.call_status,
      });
    } else if (buildingState?.call_status === CallStatusENUM.CHECKING) {
      setState({ ...state, callStatus: buildingState?.call_status });
    }
  }, [buildingState?.call_status]);
  const onAgoraClose = () => {
    setState((prevState) => ({
      ...prevState,
      agoraOpen: false,
    }));
  };

  const drawer = (
    <ListContainer>
      <Typography color="textPrimary" variant="h4">
        Monitor
      </Typography>
      <List>
        <ListItem
          button
          key={"Live Screen"}
          onClick={() => {
            history.push("/app/livescreen");
          }}
        >
          <ListItemIcon className={classes.listItemIconDrawer}>
            <Visibility />
          </ListItemIcon>
          <ListItemText
            disableTypography
            primary={<Typography variant="h5">Live Screen</Typography>}
          />
        </ListItem>
        <ListItem
          button
          key={"Shift Playback"}
          onClick={() => {
            history.push("/app/shift_playback");
          }}
        >
          <ListItemIcon className={classes.listItemIconDrawer}>
            <LiveTv />
          </ListItemIcon>
          <ListItemText
            primary={<Typography variant="h5">Shift Playback</Typography>}
          />
        </ListItem>
        <ListItem
          button
          key={"Reports"}
          onClick={() => {
            history.push("/app/reports");
          }}
        >
          <ListItemIcon className={classes.listItemIconDrawer}>
            <AssessmentOutlined />
          </ListItemIcon>
          <ListItemText
            primary={<Typography variant="h5">Reports</Typography>}
          />
        </ListItem>
        <ListItem button onClick={handleDataClick}>
          <ListItemIcon className={classes.listItemIconDrawer}>
            <HistoryOutlined />
          </ListItemIcon>
          <ListItemText primary={<Typography variant="h5">Data</Typography>} />
          {dataOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
        <Collapse in={dataOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItem
              button
              key={"Shifts"}
              onClick={() => {
                history.push("/app/data/shifts");
              }}
              className={classes.nestedDrawer}
            >
              <ListItemText
                primary={<Typography variant="h5">Shifts</Typography>}
              />
            </ListItem>
            <ListItem
              button
              key={"Shifts"}
              onClick={() => {
                history.push("/app/data/shifts_grid");
              }}
              className={classes.nestedDrawer}
            >
              <ListItemText
                primary={<Typography variant="h5">Shifts Grid</Typography>}
              />
            </ListItem>
            <ListItem
              button
              key={"Incidents"}
              onClick={() => {
                history.push("/app/data/incidents");
              }}
              className={classes.nestedDrawer}
            >
              <ListItemText
                primary={<Typography variant="h5">Incidents</Typography>}
              />
            </ListItem>

            <ListItem
              button
              key={"Tasks"}
              onClick={() => {
                history.push("/app/data/tasks");
              }}
              className={classes.nestedDrawer}
            >
              <ListItemText
                primary={<Typography variant="h5">Tasks</Typography>}
              />
            </ListItem>

            <ListItem
              button
              key={"Patrols"}
              onClick={() => {
                history.push("/app/data/patrols");
              }}
              className={classes.nestedDrawer}
            >
              <ListItemText
                primary={<Typography variant="h5">Patrols</Typography>}
              />
            </ListItem>
          </List>
        </Collapse>
      </List>
      <VerticalSpacer spacing="16px" />
      <Typography color="textPrimary" variant="h5">
        Configure
      </Typography>
      <List>
        <ListItem button onClick={handleBuildingClick}>
          <ListItemIcon className={classes.listItemIconDrawer}>
            <BusinessOutlined />
          </ListItemIcon>
          <ListItemText
            primary={<Typography variant="h5">Building</Typography>}
          />
          {buildingOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
        <Collapse in={buildingOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItem
              button
              className={classes.nestedDrawer}
              onClick={() => {
                history.push("/app/building/pois");
              }}
            >
              <ListItemText
                primary={
                  <Typography variant="h5">Points of Interest</Typography>
                }
              />
            </ListItem>
            <ListItem
              button
              className={classes.nestedDrawer}
              onClick={() => {
                history.push("/app/building/zones");
              }}
            >
              <ListItemText
                primary={<Typography variant="h5">Zones</Typography>}
              />
            </ListItem>
          </List>
        </Collapse>
        <ListItem
          button
          key={"Tasks"}
          onClick={() => {
            history.push("/app/tasks");
          }}
        >
          <ListItemIcon className={classes.listItemIconDrawer}>
            <AssignmentTurnedInOutlined />
          </ListItemIcon>
          <ListItemText primary={<Typography variant="h5">Tasks</Typography>} />
        </ListItem>
        <ListItem
          button
          key={"Roles"}
          onClick={() => {
            history.push("/app/roles");
          }}
        >
          <ListItemIcon className={classes.listItemIconDrawer}>
            <AssignmentIndOutlined />
          </ListItemIcon>
          <ListItemText primary={<Typography variant="h5">Roles</Typography>} />
        </ListItem>
        <ListItem
          button
          key={"Logbooks"}
          onClick={() => {
            history.push("/app/logbooks");
          }}
        >
          <ListItemIcon className={classes.listItemIconDrawer}>
            <CreateOutlined />
          </ListItemIcon>
          <ListItemText
            primary={<Typography variant="h5">Logbooks</Typography>}
          />
        </ListItem>
        <ListItem
          button
          key={"Users"}
          onClick={() => {
            history.push("/app/users");
          }}
        >
          <ListItemIcon className={classes.listItemIconDrawer}>
            <PermIdentityOutlined />
          </ListItemIcon>
          <ListItemText primary={<Typography variant="h5">Users</Typography>} />
        </ListItem>
      </List>
    </ListContainer>
  );

  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <div className={classes.root}>
      <CssBaseline />
      <AgoraPTT
        open={state.agoraOpen}
        onCancel={onAgoraClose}
        callStatus={state.callStatus}
      />
      <AppBar position="fixed" className={classes.appBarDrawer}>
        <Toolbar>
          <ToolbarContainer>
            <IconButton
              color="primary"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              className={classes.menuButtonDrawer}
            >
              <MenuIcon />
            </IconButton>
            <Typography color="secondary" variant="h1" noWrap>
              ioLoc
            </Typography>
            <Typography variant="h1" noWrap>
              {pageName}
            </Typography>
            <DesktopButtons>
              {/* <IconButton
                color="primary"
                aria-label="Start Call"
                edge="end"
                onClick={handleCallClick}
              >
                <Call />
              </IconButton> */}
              <IconButton
                color="primary"
                aria-label="Sign Out"
                edge="end"
                onClick={handleSignOutClick}
              >
                <ExitToApp />
              </IconButton>
            </DesktopButtons>
          </ToolbarContainer>
        </Toolbar>
      </AppBar>
      <nav className={classes.drawerDrawer} aria-label="mailbox folders">
        {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
        <Hidden smUp implementation="css">
          <Drawer
            container={container}
            variant="temporary"
            anchor={theme.direction === "rtl" ? "right" : "left"}
            open={mobileOpen}
            onClose={handleDrawerToggle}
            classes={{
              paper: classes.drawerPaperDrawer,
            }}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
          >
            {drawer}
          </Drawer>
        </Hidden>
        <Hidden xsDown implementation="css">
          <Drawer
            classes={{
              paper: classes.drawerPaperDrawer,
            }}
            variant="permanent"
            open
          >
            {drawer}
          </Drawer>
        </Hidden>
      </nav>
      <main className={classes.contentDrawer}>
        <div className={classes.toolbarDrawer} />
        {props.children}
      </main>
    </div>
  );
}
